/**
 * Admin Authentication Service
 * Handles admin PIN and password verification with Supabase database
 */

import { supabase } from "@/integrations/supabase/client";
import { hashPassword, verifyPassword } from "@/utils/passwordHash";

export interface AdminCredentials {
  email: string;
  passwordHash: string;
  pinHash: string;
}

export const adminAuthService = {
  /**
   * Setup or update admin credentials in database
   */
  async setupCredentials(email: string, password: string, pin: string): Promise<{ success: boolean; error?: string }> {
    try {
      const passwordHash = await hashPassword(password);
      const pinHash = await hashPassword(pin);

      // Call the database function to upsert credentials
      const { error } = await supabase.rpc('setup_admin_security', {
        p_email: email,
        p_password_hash: passwordHash,
        p_pin_hash: pinHash
      });

      if (error) {
        console.error('Failed to setup admin credentials:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error: any) {
      console.error('Error setting up credentials:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Verify PIN against database
   */
  async verifyPIN(pin: string): Promise<{ valid: boolean; email?: string }> {
    try {
      const pinHash = await hashPassword(pin);

      const { data, error } = await supabase
        .from('admin_security')
        .select('admin_email')
        .eq('pin_hash', pinHash)
        .single();

      if (error || !data) {
        return { valid: false };
      }

      return { valid: true, email: data.admin_email };
    } catch (error) {
      console.error('Error verifying PIN:', error);
      return { valid: false };
    }
  },

  /**
   * Verify password against database
   */
  async verifyPassword(email: string, password: string): Promise<boolean> {
    try {
      const passwordHash = await hashPassword(password);

      const { data, error } = await supabase
        .from('admin_security')
        .select('password_hash')
        .eq('admin_email', email)
        .eq('password_hash', passwordHash)
        .single();

      if (error || !data) {
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error verifying password:', error);
      return false;
    }
  },

  /**
   * Check if admin credentials exist in database
   */
  async hasCredentials(): Promise<boolean> {
    try {
      const { count, error } = await supabase
        .from('admin_security')
        .select('*', { count: 'exact', head: true });

      if (error) {
        console.error('Error checking credentials:', error);
        return false;
      }

      return (count || 0) > 0;
    } catch (error) {
      console.error('Error checking credentials:', error);
      return false;
    }
  },

  /**
   * Get admin email from database
   */
  async getAdminEmail(): Promise<string | null> {
    try {
      const { data, error } = await supabase
        .from('admin_security')
        .select('admin_email')
        .limit(1)
        .single();

      if (error || !data) {
        return null;
      }

      return data.admin_email;
    } catch (error) {
      console.error('Error getting admin email:', error);
      return null;
    }
  },

  /**
   * Enable/disable 2FA
   */
  async update2FA(email: string, enabled: boolean, secret?: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('admin_security')
        .update({
          two_factor_enabled: enabled,
          two_factor_secret: secret || null
        })
        .eq('admin_email', email);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }
};
